import '../index.css'

import React from 'react'
import ReactDOM from 'react-dom'
import {Provider} from 'react-redux'
import {CacheProvider, ThemeProvider} from '@emotion/react'
import initializeAnalytics from 'modules/analytics/initializeSDK'
import Settings from 'Settings'

import {DeviceLayoutProvider} from '@daedalus/atlas/context/deviceLayout'
import GridGlobalStyles from '@daedalus/atlas/foundation/GridGlobalStyles'
import {CssBaseline} from '@daedalus/atlas/helpers/CssBaseline'
import FontsLoader from '@daedalus/atlas/helpers/FontsLoader'
import {getTheme} from '@daedalus/atlas/themes'
import {CSSThemeProvider} from '@daedalus/atlas/themes/CSSThemeProvider'
import {
  getBrandTheme,
  getIsBrandVio
} from '@daedalus/core/src/_web/brand/modules/selectors'
import InitializeDataDogClient from '@daedalus/core/src/analytics/components/DataDogClient'
import FullStoryLoader from '@daedalus/core/src/analytics/components/FullStoryLoader'
import {brands} from '@daedalus/core/src/brand/config'
import OpticksProvider from '@daedalus/core/src/experiments/components/OpticksProvider'
import fallbackDataFile from '@daedalus/core/src/experiments/modules/fallbackDataFile'
import ConfigureI18n from '@daedalus/core/src/localization/components/ConfigureI18n'
import LanguageConfig from '@daedalus/core/src/localization/components/LanguageConfig'
import {getTextDirection} from '@daedalus/core/src/localization/services/locale'
import {evaluateInRuntime} from '@daedalus/core/src/utils/css/emotion-rtl'

import configureStore from '../configureStore'
import {Language} from '../types/partnersApi/types'
import {GlobalCrashScreen, WidgetCrashScreen} from './CrashScreen'
import {ErrorBoundary} from './ErrorBoundary'
import {InitializeABTesting} from './InitializeABTesting'

interface BaseProps {
  language: NonNullable<Language>
  userCountry?: string
  anonymousId: string
  brandCode: string
}

type RenderWidget = <Params extends BaseProps>(
  widget: (params: Params) => JSX.Element,
  initProps: Params
) => void

export const renderWidget: RenderWidget = (Widget, initProps) => {
  const {anonymousId} = initProps
  initializeAnalytics(anonymousId)

  const store = configureStore({})

  const state = store.getState()
  const brandTheme = getBrandTheme(state)
  const brandIsVio = getIsBrandVio(state)

  const websiteTheme = getTheme(brandTheme)

  const locale = initProps.language
  const {isRtl} = getTextDirection(locale)

  const rootContainer = document.querySelector('#root')
  if (!rootContainer) {
    throw new Error("Could not find element with id 'root'.")
  }

  // eslint-disable-next-line react/no-deprecated
  ReactDOM.render(
    <Provider store={store}>
      <ErrorBoundary fallback={<GlobalCrashScreen />}>
        <InitializeDataDogClient
          clientToken={Settings.get('REACT_APP_DD_CLIENT_TOKEN')}
          appVersion={Settings.get('REACT_APP_VERSION')}
          appEnv={Settings.get('REACT_APP_ENV')}
          applicationId={Settings.get('REACT_APP_DD_RUM_APP_ID')}
          sampleRate={Number(Settings.get('REACT_APP_DD_SAMPLE_RATE'))}
          premiumSampleRate={Number(
            Settings.get('REACT_APP_DD_REPLAY_SAMPLE_RATE')
          )}
          service="partners"
        />

        <OpticksProvider
          dataFileUrl={Settings.get('REACT_APP_OPTIMIZELY_DATAFILE_URL')}
          fallbackDataFile={fallbackDataFile}
          disableOptimizelyLogs={process.env.NODE_ENV === 'production'}
        >
          <InitializeABTesting>
            <ConfigureI18n
              languageCode={locale}
              countryCode={initProps?.userCountry || 'US'}
            >
              <ThemeProvider theme={websiteTheme}>
                <CSSThemeProvider isGlobal>
                  <DeviceLayoutProvider>
                    <FullStoryLoader publicUrl={'/'} isPartnersWidget />
                    <LanguageConfig locale={locale} />
                    <CacheProvider value={evaluateInRuntime(isRtl)}>
                      <GridGlobalStyles />
                      <CssBaseline />
                      <FontsLoader brand={brands.vio} languageCode={locale} />
                      <ErrorBoundary
                        fallback={
                          <WidgetCrashScreen hideSupportLink={!brandIsVio} />
                        }
                      >
                        <Widget {...initProps} />
                      </ErrorBoundary>
                    </CacheProvider>
                  </DeviceLayoutProvider>
                </CSSThemeProvider>
              </ThemeProvider>
            </ConfigureI18n>
          </InitializeABTesting>
        </OpticksProvider>
      </ErrorBoundary>
    </Provider>,
    document.querySelector('#root')
  )
}
